import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";

// ---------------------------------------------------------
// Pequeño debounce para no spamear updateDoc al tipear orden
// ---------------------------------------------------------
function useDebouncedCallback(cb, delay = 450) {
  const t = useRef(null);

  return (...args) => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => cb(...args), delay);
  };
}

export default function CarruselHomeAdmin() {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [orden, setOrden] = useState(10);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // cache local de orden (para input controlado por id)
  const [ordenDraft, setOrdenDraft] = useState({});

  const colRef = useMemo(() => collection(db, "carrusel_home"), []);

  // =========================
  // Listener en tiempo real
  // =========================
  useEffect(() => {
    const q = query(colRef, orderBy("orden", "asc"));
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(list);

      // inicializa drafts si no existen
      setOrdenDraft((prev) => {
        const next = { ...prev };
        for (const it of list) {
          if (next[it.id] === undefined) next[it.id] = it.orden ?? 0;
        }
        // limpia drafts de docs que ya no existen
        const ids = new Set(list.map((x) => x.id));
        for (const k of Object.keys(next)) if (!ids.has(k)) delete next[k];
        return next;
      });
    });
  }, [colRef]);

  const handlePickFile = (f) => {
    if (!f) return;
    setFile(f);
  };

  // =========================
  // Upload
  // =========================
  const handleUpload = async () => {
    if (!file || loadingUpload) return;

    setLoadingUpload(true);
    try {
      const storagePath = `hero_slider/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await addDoc(colRef, {
        url,
        storagePath,
        titulo: "",
        activo: true,
        orden: Number(orden) || 10,
        creadoEn: serverTimestamp(),
        actualizadoEn: serverTimestamp(),
      });

      setFile(null);
      setOrden((o) => Number(o) + 10);
    } finally {
      setLoadingUpload(false);
    }
  };

  // =========================
  // Toggle activo
  // =========================
  const toggleActivo = async (id, activo) => {
    await updateDoc(doc(db, "carrusel_home", id), {
      activo: !activo,
      actualizadoEn: serverTimestamp(),
    });
  };

  // =========================
  // Update orden (debounced)
  // =========================
  const debouncedSaveOrden = useDebouncedCallback(async (id, value) => {
    await updateDoc(doc(db, "carrusel_home", id), {
      orden: Number(value) || 0,
      actualizadoEn: serverTimestamp(),
    });
  }, 450);

  const onChangeOrden = (id, value) => {
    setOrdenDraft((p) => ({ ...p, [id]: value }));
    debouncedSaveOrden(id, value);
  };

  // =========================
  // Remove
  // =========================
  const remove = async (item) => {
    if (!window.confirm("¿Eliminar esta imagen del carrusel?")) return;

    if (item.storagePath) {
      await deleteObject(ref(storage, item.storagePath)).catch(() => {});
    }
    await deleteDoc(doc(db, "carrusel_home", item.id));
  };

  // Preview local del archivo
  const localPreview = useMemo(() => {
    if (!file) return "";
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  return (
    <div className="space-y-6">
      {/* =========================
          Uploader moderno
      ========================== */}
      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              Carrusel de Inicio
            </h3>
            <p className="text-sm text-slate-500">
              Sube imágenes, activa/desactiva slides y ordena su aparición.
            </p>
          </div>

          <div className="flex items-center gap-2 sm:self-start">
            <span className="text-xs text-slate-500">Sugerencia:</span>
            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-700">
              Orden 10, 20, 30...
            </span>
          </div>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-3">
          {/* Dropzone */}
          <div className="md:col-span-2">
            <div
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                if (f) handlePickFile(f);
              }}
              className={[
                "rounded-2xl border-2 border-dashed p-4 transition overflow-hidden",
                dragOver ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-slate-50",
              ].join(" ")}

            >
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-full md:w-44 h-28 rounded-xl bg-white border overflow-hidden flex items-center justify-center">
                  {localPreview ? (
                    <img
                      src={localPreview}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-xs text-slate-500 px-3 text-center">
                      Preview
                    </div>
                  )}
                </div>

                <div className="flex-1 w-full">
                  <p className="text-sm font-semibold text-slate-900">
                    Arrastra una imagen aquí
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    o selecciónala desde tu equipo.
                  </p>

                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <label className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold cursor-pointer hover:opacity-90">
                      Elegir archivo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePickFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>

                    <div className="flex-1 text-sm text-slate-700 px-3 py-2 rounded-xl bg-white border">
                      {file ? (
                        <span className="font-medium">{file.name}</span>
                      ) : (
                        <span className="text-slate-400">Ningún archivo seleccionado</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Orden + botón */}
          <div className="rounded-2xl border bg-white p-4 h-full">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">
                Orden inicial
              </span>
              <input
                type="number"
                value={orden}
                onChange={(e) => setOrden(e.target.value)}
                className="mt-2 w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>

            <button
              onClick={handleUpload}
              disabled={!file || loadingUpload}
              className={[
                "mt-4 w-full rounded-xl px-4 py-2 text-sm font-semibold transition",
                !file || loadingUpload
                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700",
              ].join(" ")}
            >
              {loadingUpload ? "Subiendo…" : "Agregar al carrusel"}
            </button>

            <p className="mt-3 text-xs text-slate-500">
              Consejo: usa saltos de 10 para reordenar fácil.
            </p>
          </div>
        </div>
      </div>

      {/* =========================
          Lista moderna
      ========================== */}
      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-bold text-slate-900">Slides</h3>
          <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">
            {items.length} total
          </span>
        </div>

        {items.length === 0 ? (
          <div className="mt-4 rounded-xl border bg-slate-50 p-4 text-sm text-slate-600">
            Aún no hay imágenes en el carrusel.
          </div>
        ) : (
          <div className="mt-4 grid gap-4">
            {items.map((it) => (
              <div
                key={it.id}
                className="rounded-2xl border p-4 hover:shadow-sm transition bg-white overflow-hidden"
              >
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Thumb */}
                  <div className="w-full lg:w-52 shrink-0">
                    <div className="aspect-[4/3] rounded-xl overflow-hidden border bg-slate-50">
                      <img
                        src={it.url}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* Info + controles */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between gap-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">
                            Slide
                          </span>

                          <span
                            className={[
                              "text-xs font-semibold px-2 py-1 rounded-full",
                              it.activo
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-slate-100 text-slate-600 border border-slate-200",
                            ].join(" ")}
                          >
                            {it.activo ? "Activo" : "Inactivo"}
                          </span>
                        </div>

                          <p className="text-xs text-slate-500 mt-1 truncate">
                            {it.storagePath || "—"}
                          </p>
                      </div>

                      {/* Switch */}
                      <button
                        onClick={() => toggleActivo(it.id, it.activo)}
                        className={[
                          "relative inline-flex h-8 w-14 items-center rounded-full transition border",
                          it.activo
                            ? "bg-blue-600 border-blue-600"
                            : "bg-slate-200 border-slate-200",
                        ].join(" ")}
                        title={it.activo ? "Desactivar" : "Activar"}
                      >
                        <span
                          className={[
                            "inline-block h-6 w-6 transform rounded-full bg-white transition",
                            it.activo ? "translate-x-7" : "translate-x-1",
                          ].join(" ")}
                        />
                      </button>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      {/* Orden */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-600">
                          Orden
                        </span>
                        <input
                          type="number"
                          value={ordenDraft[it.id] ?? it.orden ?? 0}
                          onChange={(e) => onChangeOrden(it.id, e.target.value)}
                          className="w-28 rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                        />
                        <span className="text-xs text-slate-400">
                          (se guarda solo)
                        </span>
                      </div>

                      <div className="flex-1" />

                      {/* Acciones */}
                      <button
                        onClick={() => remove(it)}
                        className="inline-flex justify-center rounded-xl px-4 py-2 text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
